import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Formik } from 'formik'
import * as Yup from 'yup'

import {
  CsiCustomersFilter,
  Customer,
  NamedItem,
  NamedItemInt,
  PagedCustomers,
  ProductIds,
  getCustomers,
  impersonate,
} from 'core'

import { Button, Card, Col, DefaultInputV2, DefaultSelectV2, Icon, Row, TableV2 } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { useCommandRequestV2, useQueryParamsV2, useQueryRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import RecurlyLogo from '../../../assets/images/RecurlyLogo'
import SalesforceLogo from '../../../assets/images/SalesforceLogo'

export const CSI_SEARCH_PATH = 'csi-search'

const CsiSearch: React.FC = () => {
  const { t: translate } = useTranslation()
  const history = useHistory()
  const { searchValue } = useQueryParamsV2()

  const [filter, setFilter] = useState<CsiCustomersFilter>({
    pageNumber: 1,
    pageSize: 10,
    orderColumn: 'createdAt',
    orderDescending: false,
    q: searchValue ?? '',
    productId: undefined,
    signupCompleted: undefined,
    hasActivePlanPeriod: undefined,
  })

  const {
    data: pagedCustomers,
    isLoading: isCustomersLoading,
    sendQuery: sendGetCustomersRequest,
  } = useQueryRequestV2<PagedCustomers>({
    initialState: undefined,
  })

  const { sendCommand, isLoading: isCommandLoading } = useCommandRequestV2((userId: string) => impersonate(userId))

  const impersonateUser = async (userId: string, companyId: string, companyName: string) => {
    const result = await sendCommand(userId)
    if (result.completed) {
      window.open(`/?token=${result.data.token}`, '_blank')
    }
  }

  useEffect(() => {
    ;(async () => {
      if (
        filter.q ||
        filter.productId != undefined ||
        filter.signupCompleted != undefined ||
        filter.hasActivePlanPeriod != undefined
      ) {
        await sendGetCustomersRequest(() => getCustomers({ ...filter }))
      }
    })()
  }, [filter])

  const columns: any[] = [
    {
      title: 'Customer Id',
      dataIndex: 'companyId',
      key: 'companyId',
      sorter: true,
      render: (text: string, record: Customer) => {
        return (
          <div>
            {record.signupCompleted ? (
              <p
                className="cursor_pointer"
                onClick={() =>
                  history.push(
                    `${ROUTE_PATHS.CSI_COMPANY_DETAILS}/${record.companyId}?companyName=${record.name}&searchValue=${filter.q}&productId=${record.products[0].id}`,
                  )
                }
              >
                {record.companyId}
              </p>
            ) : (
              <p> {record.companyId}</p>
            )}
          </div>
        )
      },
    },
    {
      title: 'Customer name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
    },
    {
      title: 'Products',
      dataIndex: 'products',
      key: 'products',
      sorter: false,
      render: (products: NamedItem[], record: Customer) => products.map((x) => x.name).join(','),
    },
    {
      align: 'center',
      title: 'Signup completed',
      dataIndex: 'signupCompleted',
      key: 'signupCompleted',
      sorter: true,
      render: (text: string, record: Customer) => (record.signupCompleted ? 'True' : 'False'),
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (createdAt: string) => parseZoneFormat(createdAt),
    },
    {
      align: 'center',
      title: 'Impersonate Admin',
      dataIndex: 'primaryAdmins',
      key: 'primaryAdmins',
      sorter: false,
      render: (
        primaryAdmins: { companyUserId: string; product: NamedItemInt; firstName: string; lastName: string }[],
        record: any,
      ) => (
        <>
          {primaryAdmins.map((x) => (
            <div className={'mb-8'}>
              <Button
                className={'w-100'}
                onClick={() => impersonateUser(x.companyUserId, record.companyId, record.name)}
                disabled={isCommandLoading}
                loading={isCommandLoading}
              >{`${x.firstName}(${x.product.name})`}</Button>
            </div>
          ))}
        </>
      ),
    },
    {
      align: 'center',
      title: 'External',
      dataIndex: 'recurlyCustomerUrl',
      key: 'external',
      sorter: false,
      render: (_: string, record: any) => (
        <>
          <div className={'mb-8 w-100'}>
            <Button
              type="default"
              className={'w-100'}
              onClick={() => window.open(record.recurlyCustomerUrl, '_blank')}
              customIcon={<RecurlyLogo />}
            >
              {translate('general.recurly')}
            </Button>
          </div>

          <div className={'w-100'}>
            <Button
              type="default"
              className={'w-100'}
              onClick={() => window.open(record.salesforceCustomerUrl, '_blank')}
              customIcon={<SalesforceLogo />}
            >
              {translate('general.salesforce')}
            </Button>
          </div>
        </>
      ),
    },
  ]

  const columnToSort: any = columns.find((c) => c.key === filter.orderColumn)
  if (columnToSort) {
    columnToSort.defaultSortOrder = filter.orderDescending ? 'descend' : 'ascend'
  }

  const onTableChange = (pagination: any, filters: any, sorter: any) => {
    setFilter({
      ...filter,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field || 'createdAt',
      orderDescending: sorter.order !== 'ascend',
    })
  }

  return (
    <div data-testid={'csi-search-page'}>
      <Card title={translate('csi-dashboard.csi-search.title')}>
        <Formik
          enableReinitialize={true}
          validationSchema={Yup.object({})}
          initialValues={filter}
          onSubmit={(values) => {
            setFilter({
              ...filter,
              ...values,
              pageNumber: 1,
            })
          }}
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit}>
              <Row gutter={16}>
                <Col span={8}>
                  <DefaultInputV2
                    suffix={<Icon type="fal fa-search" fontAwesome />}
                    onKeyUp={async (e: any) => {
                      if (e.keyCode == 13) await formik.submitForm()
                    }}
                    className="mr-8"
                    placeholder="Search by company name or id, employee id or work email"
                    name="q"
                  />
                </Col>

                <Col span={4}>
                  <DefaultSelectV2
                    name="productId"
                    placeholder={'Select product'}
                    label={null}
                    items={[
                      { id: null, name: 'All' },
                      { id: ProductIds.IchraId, name: 'ICHRA' },
                      { id: ProductIds.QsehraId, name: 'QSEHRA' },
                      { id: ProductIds.GroupHraId, name: 'GCHRA' },
                      { id: ProductIds.Perks, name: 'PERKS' },
                    ]}
                  ></DefaultSelectV2>
                </Col>

                <Col span={4}>
                  <DefaultSelectV2
                    name="signupCompleted"
                    placeholder={'Signup Completed?'}
                    label={null}
                    items={[
                      { id: null, name: 'All' },
                      { id: true, name: 'Signup Completed' },
                      { id: false, name: 'NOT Signup Completed' },
                    ]}
                  ></DefaultSelectV2>
                </Col>

                <Col span={4}>
                  <DefaultSelectV2
                    name="hasActivePlanPeriod"
                    placeholder={'Has Active Benefit?'}
                    label={null}
                    items={[
                      { id: null, name: 'All' },
                      { id: true, name: 'Has Active Benefit' },
                      { id: false, name: 'No Active Benefit' },
                    ]}
                  ></DefaultSelectV2>
                </Col>

                <Col>
                  <Button
                    type={'primary'}
                    htmlType="submit"
                    disabled={
                      (!formik.values?.q &&
                        !formik?.values.productId == undefined &&
                        !formik.values.signupCompleted == undefined) ||
                      isCustomersLoading
                    }
                    loading={isCustomersLoading}
                  >
                    Search
                  </Button>
                </Col>
              </Row>
            </form>
          )}
        </Formik>

        {pagedCustomers && (
          <>
            <h5>{pagedCustomers.totalItemCount} in total</h5>

            <TableV2
              dataSource={pagedCustomers.items}
              onChange={onTableChange}
              loading={isCustomersLoading}
              columns={columns}
              pagination={{
                total: pagedCustomers.totalItemCount,
                pageSize: pagedCustomers.pageSize,
                current: pagedCustomers.pageNumber,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '30', '40'],
              }}
            />
          </>
        )}
      </Card>
    </div>
  )
}

export default CsiSearch
